import { useContext, useLayoutEffect, useRef, useState } from 'react'

import { Box, IconsName, Text } from '@fairfashion/design-system'

import { ProvidersMap } from '../../components/ProvidersMap/providers-map.comp'

import {
  DashboardContext,
  DashboardProvider as DashboardProviderContext,
} from './dashboard.context'
import * as Styles from './dashboard.styles'
import {
  feedstockItemsTracedToEndSupplierQuantityPropPercentageDetails,
  finishedItemProfilesQuantitySumDetails,
  finishedItemsQuantitySumDetails,
  finishedItemsTracedToEndSupplierQuantityPropPercentageDetails,
  itemsTransactionHashesQuantitySumDetails,
  providersWithRiskDetails,
} from './dashboard.utils'

const DashboardComp = () => {
  const [rightColumnHeight, setRightColumnHeight] = useState<number>(0)

  const leftColumnRef = useRef<HTMLDivElement>(null)

  const { statistics } = useContext(DashboardContext)

  useLayoutEffect(() => {
    const handleResize = () => {
      if (leftColumnRef.current) {
        if (window.innerWidth < 1000) {
          setRightColumnHeight(400)
          return
        } else {
          setRightColumnHeight(leftColumnRef.current.clientHeight)
        }
      }
    }

    window.addEventListener('resize', handleResize)

    if (leftColumnRef.current) {
      setRightColumnHeight(leftColumnRef.current.clientHeight)
    }
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Box width="100%" height="100%" padding={3} direction="column" gap={3}>
      <Box width="100%" gap={3} direction="column" paddingBottom={3}>
        <Box width="100%" gap={3} flexWrap="wrap">
          <Styles.LeftColumn ref={leftColumnRef}>
            <Box direction="column" gap={3} width="100%">
              <Box width="100%" gap={3} flexWrap="wrap" alignItems="stretch">
                <Styles.HighlightCustomCard
                  title="Fornecedores"
                  icon="building3"
                  value={statistics?.providersQuantitySum?.value ?? 0}
                  color="primary"
                  url="/providers"
                />
                <Styles.HighlightCustomCard
                  title="Fornecedores com risco"
                  icon="building"
                  value={statistics?.providersWithRisksQuantitySum?.value ?? 0}
                  color="warning"
                  details={providersWithRiskDetails}
                  url="/providers"
                />
              </Box>
              <Box width="100%" gap={3} flexWrap="wrap" alignItems="stretch">
                <Styles.PercentageCustomCard
                  title="Produtos rastreados"
                  icon={'swap2' as IconsName}
                  value={
                    statistics
                      ?.finishedItemsTracedToEndSupplierQuantityPropPercentage
                      ?.value ?? 0
                  }
                  color="lilac"
                  details={
                    finishedItemsTracedToEndSupplierQuantityPropPercentageDetails
                  }
                  url="/traceability/invoices"
                />
                <Styles.PercentageCustomCard
                  title="Material rastreado"
                  icon="focus2"
                  value={
                    statistics
                      ?.feedstockItemsTracedToEndSupplierQuantityPropPercentage
                      ?.value ?? 0
                  }
                  color="orange"
                  details={
                    feedstockItemsTracedToEndSupplierQuantityPropPercentageDetails
                  }
                  url="/traceability/invoices"
                />
              </Box>
            </Box>
          </Styles.LeftColumn>

          <Styles.RightColumn>
            <Box width="100%">
              <ProvidersMap
                maxHeight={`${rightColumnHeight}px`}
                header={<Text fontWeight={500}>Fornecedores</Text>}
              />
            </Box>
          </Styles.RightColumn>
        </Box>

        <Box width="100%" gap={3} flexWrap="wrap" alignItems="stretch">
          <Styles.SecondaryCard
            title="Notas fiscais"
            icon="receipt"
            value={statistics?.invoicesQuantitySum?.value ?? 0}
            color="gray_blue"
            url="/traceability/invoices"
          />
          <Styles.SecondaryCard
            title="Ordens de compra"
            icon="newspaper"
            value={statistics?.ordersQuantitySum?.value ?? 0}
            color="blue"
            url="/traceability/invoices"
          />
          <Styles.SecondaryCard
            title="Produtos produzidos"
            icon="price_tag3"
            value={statistics?.finishedItemsQuantityPropSum?.value ?? 0}
            color="purple"
            details={finishedItemsQuantitySumDetails}
            url="/traceability/invoices"
          />
          <Styles.HighlightCustomCard
            title="Itens transacionados na blockchain"
            icon="box3"
            value={statistics?.itemsTransactionHashesQuantitySum?.value ?? 0}
            color="light_blue"
            details={itemsTransactionHashesQuantitySumDetails}
          />
        </Box>

        <Box width="100%" gap={3} flexWrap="wrap" alignItems="stretch">
          <Styles.SecondaryCard
            title="Quantidade de QRCodes"
            icon="qr_code"
            value={statistics?.finishedItemProfilesQuantitySum?.value ?? 0}
            color="gray_blue"
            details={finishedItemProfilesQuantitySumDetails}
            url="/qrcode"
            bigTitle
          />
          <Styles.SecondaryCard
            title="Quantidade de produtos rastreados"
            icon={'swap2' as IconsName}
            value={
              statistics?.finishedItemsTracedToEndSupplierQuantityPropSum
                ?.value ?? 0
            }
            color="pink"
            url="/traceability/invoices"
            bigTitle
          />
          <Styles.SecondaryCard
            title="Quantidade de material rastreado"
            icon="focus2"
            value={statistics?.feedstockItemsTracedToEndSupplier?.value ?? 0}
            color="orange"
            url="/traceability/invoices"
            bigTitle
          />
          <Styles.SecondaryCard
            title="Ordens de produção"
            icon={'swap2' as IconsName}
            value={statistics?.orderProductionQuantitySum?.value ?? 0}
            color="primary"
            url="qrcode"
            bigTitle
          />
        </Box>
      </Box>
    </Box>
  )
}

export const Dashboard = () => {
  return (
    <DashboardProviderContext>
      <DashboardComp />
    </DashboardProviderContext>
  )
}
